import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios[fieldsetIndex] = radios;

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.checked || radio.dataset.currentChecked === 'true'
      );
      this.#checkedIndices[fieldsetIndex] = initialCheckedIndex !== -1 ? [initialCheckedIndex] : [];

      const checkedRadio = radios.find((radio) => radio.checked || radio.dataset.currentChecked === 'true');
      if (checkedRadio instanceof HTMLInputElement) {
        this.#updateImageOptionSelection(checkedRadio);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#syncVariantUrlOnLoad();
  }

  /**
   * Ensures ?variant= is in the URL on load so refresh keeps the selected variant.
   */
  #syncVariantUrlOnLoad() {
    const isOnProductPage = this.dataset.templateProductMatch === 'true';
    if (!isOnProductPage) return;

    const url = new URL(window.location.href);
    const urlVariantId = url.searchParams.get('variant');

    const emitUrlSync = (variantId) => {
      document.dispatchEvent(
        new CustomEvent('variant:url-sync', {
          bubbles: true,
          detail: { variantId: String(variantId) },
        })
      );
    };

    if (urlVariantId) {
      const optionInput = this.querySelector(`input[data-variant-id="${urlVariantId}"]`);
      const mediaId = optionInput?.dataset?.optionMediaId;
      document.dispatchEvent(
        new CustomEvent('variant:url-sync', {
          bubbles: true,
          detail: {
            variantId: String(urlVariantId),
            mediaId: mediaId ? String(mediaId) : undefined,
          },
        })
      );
      return;
    }

    // Swap-first-media PDPs: keep clean URL on first load so the gallery shows the swapped hero image.
    if (this.dataset.swapFirstMedia === 'true') {
      return;
    }

    const variantJson = this.querySelector('script[type="application/json"]')?.textContent;
    if (!variantJson) return;

    let variant;
    try {
      variant = JSON.parse(variantJson);
    } catch {
      return;
    }

    if (!variant?.id) return;

    url.searchParams.set('variant', String(variant.id));
    history.replaceState({}, '', url.toString());
    emitUrlSync(variant.id);
    return;
  }

  /**
   * Fetches section HTML for a URL variant so media, ingredients, and price match on refresh.
   *
   * @param {string} variantId
   */
  #refreshSectionForVariant(variantId) {
    const productUrl = this.dataset.productUrl?.split('?')[0];
    const sectionId = this.dataset.sectionId;
    if (!productUrl || !sectionId || !variantId) return;

    requestYieldCallback(() => {
      this.fetchUpdatedSection(`${productUrl}?variant=${variantId}&section_id=${sectionId}`);
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
        document.dispatchEvent(
          new CustomEvent('variant:url-sync', {
            bubbles: true,
            detail: {
              variantId: String(variantId),
              mediaId: selectedOption.dataset.optionMediaId || undefined,
            },
          })
        );
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        if (!this.#checkedIndices[fieldsetIndex]) {
          this.#checkedIndices[fieldsetIndex] = [];
        }
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        let radios = this.#radios[fieldsetIndex];
        if (!radios) {
          radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
          this.#radios[fieldsetIndex] = radios;
        }

        if (radios && fieldset) {
          radios.forEach((radio, index) => {
            if (index !== inputIndex) {
              radio.dataset.currentChecked = 'false';
              radio.dataset.previousChecked = 'false';
            }
          });

          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
      target.dataset.currentChecked = 'true';
      this.#updateImageOptionSelection(target);
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Syncs selected styling on image-type variant options.
   *
   * @param {HTMLInputElement} input
   */
  #updateImageOptionSelection(input) {
    const fieldset = input.closest('fieldset.variant-option--image-type');
    if (!fieldset) return;

    fieldset.querySelectorAll('.variant-option__image-wrapper.is-selected').forEach((wrapper) => {
      wrapper.classList.remove('is-selected');
    });

    const wrapper = input.nextElementSibling;
    if (wrapper instanceof HTMLElement && wrapper.classList.contains('variant-option__image-wrapper')) {
      wrapper.classList.add('is-selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    const variantId = selectedOption.dataset.variantId;
    if (variantId) {
      params.push(`variant=${variantId}`);
    }

    const sectionId = this.dataset.sectionId;
    const isProductCardContext =
      this.closest('quick-add-component') || this.closest('swatches-variant-picker-component');

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (isProductCardContext) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    if (sectionId) {
      params.unshift(`section_id=${sectionId}`);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Builds a minimal variant object from the currently selected option.
   *
   * @returns {{ id: string, featured_media?: { id: string } } | null}
   */
  #variantFromSelectedOption() {
    const selectedOption = this.selectedOption;
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return null;
    }

    const { variantId, optionMediaId } = selectedOption.dataset;
    if (!variantId) return null;

    return {
      id: variantId,
      ...(optionMediaId ? { featured_media: { id: optionMediaId } } : {}),
    };
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);
          let variant = JSON.parse(textContent);

          if (!variant?.id) {
            variant = this.#variantFromSelectedOption();
          }

          let sourceId;
          try {
            sourceId = this.selectedOptionId;
          } catch {
            sourceId = variant?.id != null ? String(variant.id) : undefined;
          }

          if (sourceId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variant, sourceId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    this.#reinitializeFromDOM();

    return newProduct;
  }

  /**
   * Rebuilds picker state after morph so checked styling persists.
   */
  #reinitializeFromDOM() {
    this.#checkedIndices = [];
    this.#radios = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios[fieldsetIndex] = radios;

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.checked || radio.dataset.currentChecked === 'true'
      );
      this.#checkedIndices[fieldsetIndex] = initialCheckedIndex !== -1 ? [initialCheckedIndex] : [];

      const checkedRadio = radios.find((radio) => radio.checked || radio.dataset.currentChecked === 'true');
      if (checkedRadio instanceof HTMLInputElement) {
        this.#updateImageOptionSelection(checkedRadio);
      }
    });

    if (this.querySelector('input:checked, input[data-current-checked="true"]')) {
      this.removeAttribute('data-defer-variant-selection');
    }
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
